export type Service = {
  code: string;
  slug: string;
  kind: string;
  /** Una riga sola, al massimo una quarantina di caratteri: la
   *  spiegazione minima che accompagna il nome del servizio dove c'è
   *  spazio per una frase e non per il `body`.
   *
   *  La misura è un vincolo, non un'indicazione. Nella spalla della home
   *  le tile ruotano ogni diciotto secondi: se il claim di un servizio
   *  occupa due righe e quello del successivo tre, la colonna si alza e
   *  si abbassa da sola mentre qualcuno la sta guardando. Sotto i 40
   *  caratteri stanno tutti in due righe e la tile non cambia mai
   *  altezza. */
  claim: string;
  title: string;
  body: string;
  symbol: string;
  bullets: string[];
};

export const SERVICES: Service[] = [
  {
    code: "S01",
    slug: "monitoraggio",
    kind: "Monitoraggio",
    claim: "Scansione continua su temi e filiere",
    title: "Monitoraggio e analisi settoriale",
    body: "Elaborazione di informazioni su temi settoriali e merceologici, con elementi di geoeconomia su scala regionale e nazionale.",
    symbol: "◉",
    bullets: [
      "Scansione continua su temi e filiere",
      "Geoeconomia regionale e nazionale",
      "Segnali deboli e trend emergenti",
    ],
  },
  {
    code: "S02",
    slug: "report",
    kind: "Report",
    claim: "Dossier geostrategici su misura",
    title: "Report geostrategici",
    body: "Informazioni geostrategiche, approfondite e contestualizzate. I contenuti possono essere mirati su tematiche corporate o singoli casi.",
    symbol: "▤",
    bullets: [
      "Dossier su misura",
      "Approfondimenti corporate",
      "Contesto geopolitico per il tuo settore",
    ],
  },
  {
    code: "S03",
    slug: "verifica-informativa",
    kind: "Verifica informativa",
    claim: "Riscontro dei fatti, fonte per fonte",
    title: "Verifica informativa",
    body: "Riscontro delle informazioni attraverso tracciamento analitico e controllo dei dati contro tentativi di discredito delle aziende.",
    symbol: "⌕",
    bullets: [
      "Fact-checking multi-fonte",
      "Contrasto a disinformazione mirata",
      "Attribuzione di campagne ostili",
    ],
  },
  {
    code: "S04",
    slug: "due-diligence",
    kind: "Due Diligence",
    claim: "Chi è davvero la controparte",
    title: "Due diligence strategica",
    body: "Analisi completa dell'attività e verifica dell'integrità di un'entità partner o estera, con riferimento alla struttura aziendale e alle reti di influenza.",
    symbol: "▲",
    bullets: [
      "Profilo reputazionale e societario",
      "Mappatura reti di influenza",
      "Esposizione a sanzioni e PEP",
    ],
  },
  {
    code: "S05",
    slug: "rischio-reputazionale",
    kind: "Reputazione",
    claim: "Quello che di te si dice in rete",
    title: "Rischio reputazionale",
    body: "Controllo delle rappresentazioni in rete per tutelare i conti e le partnership aziendali contro speculazioni e attacchi mirati.",
    symbol: "◆",
    bullets: [
      "Monitoraggio web e social continuo",
      "Early warning su attacchi mirati",
      "Piani di risposta e remediation",
    ],
  },
  {
    code: "S06",
    slug: "presenza-internazionale",
    kind: "Presenza internazionale",
    claim: "Aprire e tenere una posizione",
    title: "Espansione e presenza all'estero",
    body: "Supporto all'espansione aziendale oltre confine con raccolta di informazioni tattica e mirata e l'affiancamento dei nostri esperti sul campo.",
    symbol: "◈",
    bullets: [
      "Analisi di paese e mercati target",
      "Rete di contatti qualificati",
      "Affiancamento operativo in loco",
    ],
  },
  {
    code: "S07",
    slug: "cibernetica-aziendale",
    kind: "Cibernetica",
    claim: "L'ordine prima dell'automazione",
    title: "Cibernetica aziendale",
    body: "Costruzione di un'infrastruttura informatica che possa valorizzare il capitale umano, l'esperienza e la professionalità della tua azienda.",
    symbol: "⬡",
    bullets: [
      "Architetture sicure e scalabili",
      "Workflow data-driven",
      "Protezione dei flussi informativi",
    ],
  },
  {
    code: "S08",
    slug: "pianificazione-strategica",
    kind: "Strategia",
    claim: "Obiettivi, piano, accompagnamento",
    title: "Pianificazione strategica",
    body: "Affiancamento professionale e logistico nell'analisi, nell'elaborazione e nel conseguimento di target strategici dell'azienda.",
    symbol: "§",
    bullets: [
      "Definizione obiettivi a 3-5 anni",
      "Piano operativo e KPI",
      "Accompagnamento decisionale",
    ],
  },
  {
    code: "S09",
    slug: "informatica",
    kind: "Informatica",
    claim: "Infrastruttura, dati, sicurezza",
    title: "IT e digitalizzazione",
    body: "Soluzioni IT complete per la digitalizzazione della tua azienda — dalla gestione delle infrastrutture alla sicurezza dei dati.",
    symbol: "⬢",
    bullets: [
      "Infrastruttura cloud e on-premise",
      "Cybersecurity e protezione dati",
      "Workflow digitali e automazione",
    ],
  },
  {
    code: "S10",
    slug: "giuridico",
    kind: "Giuridico",
    claim: "Contratti, conformità, contenzioso",
    title: "Consulenza legale",
    body: "Consulenza legale specializzata per proteggere la tua azienda e garantire la conformità normativa.",
    symbol: "▼",
    bullets: [
      "Contrattualistica commerciale",
      "Compliance normativa e GDPR",
      "Contenzioso e tutela societaria",
    ],
  },
  {
    code: "S11",
    slug: "analisi",
    kind: "Analisi",
    claim: "Mercati e fattibilità, in cifre",
    title: "Studi di mercato e fattibilità",
    body: "Analisi di mercato e studi di fattibilità per supportare le decisioni strategiche della tua azienda.",
    symbol: "▣",
    bullets: [
      "Scenari di mercato per settore",
      "Due diligence pre-investimento",
      "Analisi competitiva e pricing",
    ],
  },
  {
    code: "S12",
    slug: "sanzioni",
    kind: "Sanzioni",
    claim: "Con chi si può lavorare, e con chi no",
    title: "Gestione sanzioni",
    body: "Gestione delle sanzioni amministrative e supporto per la conformità normativa internazionale.",
    symbol: "※",
    bullets: [
      "Screening OFAC / UE / UK",
      "Risk assessment controparti",
      "Procedure di remediation",
    ],
  },
  {
    code: "S13",
    slug: "import-export",
    kind: "Import / Export",
    claim: "Oltre la dogana: costi e partner veri",
    title: "Import / Export",
    body: "Affianchiamo le aziende che vogliono importare o esportare ben oltre la documentazione doganale: cerchiamo referenti e partner produttivi all'estero, valutiamo qualità, prezzo e fattibilità complessiva del prodotto — tasse, trasporti, logistica, condizioni di mercato. Un prodotto a basso costo in origine non è detto sia conveniente a destinazione: il mondo dell'import/export è cambiato, oggi serve l'informazione giusta e una logistica complementare per cogliere opportunità reali e stringere partnership produttive di qualità.",
    symbol: "↗",
    bullets: [
      "Referenti e partner produttivi all'estero",
      "Valutazione di qualità, prezzo e fattibilità",
      "Logistica complementare e costi reali (tasse, trasporti)",
    ],
  },
  {
    code: "S14",
    slug: "materie-prime",
    kind: "Materie prime",
    claim: "Fornitori affidabili, fino alla consegna",
    title: "Ricerca e approvvigionamento materie prime",
    body: "Supporto alle PMI italiane nella ricerca di materie prime affidabili sui mercati internazionali — dallo scouting dei fornitori alla consegna, con tutela sanzionatoria e logistica.",
    symbol: "◇",
    bullets: [
      "Scouting fornitori globali",
      "Due diligence e compliance sanzioni",
      "Negoziazione e logistica end-to-end",
    ],
  },
];
